/**
 * Weekday Conversion Utilities for RFC 5545 RRULE Compliance
 * 
 * Provides standardized conversion between different weekday formats:
 * - Lowercase full names ('monday', 'tuesday') -> Uppercase abbreviations ('MO', 'TU')
 * - Maintains chronological sorting (SU, MO, TU, WE, TH, FR, SA)
 * 
 * RFC 5545 requires weekdays in RRULE to be uppercase 2-letter codes.
 * 
 * @see https://datatracker.ietf.org/doc/html/rfc5545#section-3.3.10
 */

/**
 * Mapping from lowercase full weekday names to RFC 5545 uppercase abbreviations
 */
export const WEEKDAY_NAME_TO_CODE: { [key: string]: string } = {
  'sunday': 'SU',
  'monday': 'MO',
  'tuesday': 'TU',
  'wednesday': 'WE',
  'thursday': 'TH',
  'friday': 'FR',
  'saturday': 'SA'
};

/**
 * Mapping from RFC 5545 uppercase abbreviations to full weekday names
 */
export const WEEKDAY_CODE_TO_NAME: { [key: string]: string } = {
  'SU': 'Sunday',
  'MO': 'Monday',
  'TU': 'Tuesday',
  'WE': 'Wednesday',
  'TH': 'Thursday',
  'FR': 'Friday',
  'SA': 'Saturday'
};

/**
 * Chronological order of weekdays (Sunday first)
 */
export const WEEKDAY_ORDER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/**
 * Converts weekday values to RFC 5545 compliant uppercase abbreviations
 * Handles both lowercase full names and already-converted abbreviations
 * 
 * @param day - Weekday in any format ('monday', 'MO', 'Monday')
 * @returns Uppercase 2-letter abbreviation ('MO') or original if unknown
 * 
 * @example
 * convertWeekdayToCode('monday') // 'MO'
 * convertWeekdayToCode('MO')     // 'MO'
 * convertWeekdayToCode('Monday') // 'MO'
 */
export function convertWeekdayToCode(day: string): string {
  const trimmed = day.trim();
  
  // Already in correct format (2 uppercase letters)
  if (trimmed.length === 2 && /^[A-Z]{2}$/.test(trimmed)) {
    return trimmed;
  }
  
  // Convert from lowercase full name
  return WEEKDAY_NAME_TO_CODE[trimmed.toLowerCase()] || trimmed.toUpperCase();
}

/**
 * Converts and sorts weekdays to RFC 5545 compliant format
 * Removes duplicates and maintains chronological order (Sunday first)
 * 
 * @param days - Array of weekdays in any format
 * @returns Sorted array of uppercase 2-letter abbreviations
 * 
 * @example
 * convertAndSortWeekdays(['monday', 'wednesday', 'friday'])
 * // Returns: ['MO', 'WE', 'FR']
 * 
 * convertAndSortWeekdays(['thursday', 'tuesday', 'thursday'])
 * // Returns: ['TU', 'TH'] (sorted, duplicates removed)
 */
export function convertAndSortWeekdays(days: string[]): string[] {
  // Convert all days to RFC 5545 format
  const converted = days
    .map(convertWeekdayToCode)
    .filter(Boolean); // Remove any empty strings
  
  // Remove duplicates
  const unique = Array.from(new Set(converted));
  
  // Sort chronologically (Sunday first)
  return unique.sort((a, b) => {
    return WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b);
  });
}

/**
 * Normalizes BYDAY parameter in RRULE string to RFC 5545 format
 * Converts lowercase weekday names to uppercase abbreviations
 * 
 * @param rruleString - RRULE string that may contain lowercase weekday names
 * @returns RRULE string with normalized BYDAY parameter
 * 
 * @example
 * normalizeRRuleByday('FREQ=WEEKLY;BYDAY=monday,wednesday')
 * // Returns: 'FREQ=WEEKLY;BYDAY=MO,WE'
 * 
 * normalizeRRuleByday('FREQ=WEEKLY;BYDAY=MO,WE')
 * // Returns: 'FREQ=WEEKLY;BYDAY=MO,WE' (already normalized)
 */
export function normalizeRRuleByday(rruleString: string): string {
  return rruleString.replace(/BYDAY=([^;]+)/i, (match, days) => {
    const normalizedDays = days
      .split(',')
      .map((day: string) => convertWeekdayToCode(day))
      .join(',');
    
    return `BYDAY=${normalizedDays}`;
  });
}

/**
 * Converts RFC 5545 weekday codes to human-readable full names
 * 
 * @param codes - Array of uppercase 2-letter abbreviations
 * @returns Array of full weekday names
 * 
 * @example
 * convertCodesToNames(['MO', 'WE', 'FR'])
 * // Returns: ['Monday', 'Wednesday', 'Friday']
 */
export function convertCodesToNames(codes: string[]): string[] {
  return codes
    .map(code => WEEKDAY_CODE_TO_NAME[code] || code)
    .filter(Boolean);
}

/**
 * Formats weekday codes as human-readable comma-separated string
 * 
 * @param codes - Array of uppercase 2-letter abbreviations
 * @returns Formatted string like "Monday, Wednesday, Friday"
 * 
 * @example
 * formatWeekdaysForDisplay(['MO', 'WE', 'FR'])
 * // Returns: "Monday, Wednesday, Friday"
 */
export function formatWeekdaysForDisplay(codes: string[]): string {
  return convertCodesToNames(codes).join(', ');
}


